/**
 * Заглушка вместо вставки видео.
 *
 * Показывает обложку с кнопкой и подставляет проигрыватель только
 * по нажатию.
 *
 * Зачем. Каждая вставка YouTube тянет около мегабайта чужого кода
 * и добавляет два-три домена — ещё до того, как посетитель решит
 * что-то смотреть. Пять роликов на главной означали бы пять таких
 * вставок сразу. У главной и так 453 запроса и 8,5 секунды до полной
 * загрузки (замеры в docs/ux-audit-mobile-desktop.md), и лента видео
 * не должна делать это хуже.
 *
 * После нажатия iframe получает autoplay — иначе человеку пришлось бы
 * нажимать дважды: один раз на нашу кнопку, второй на кнопку YouTube.
 */
if (!customElements.get('video-facade')) {
  customElements.define(
    'video-facade',
    class VideoFacade extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', () => this.load());

        // Нажатие идёт по всей карточке, но кнопка обязана оставаться
        // настоящей кнопкой: с неё работают клавиатура и озвучка.
        const button = this.querySelector('.video-facade__play');
        if (button) {
          button.addEventListener('click', (e) => {
            e.stopPropagation();
            this.load();
          });
        }

        this.watchPoster();
      }

      /**
       * Рисует обложку, когда лента подошла к экрану.
       *
       * Обычный <img loading="lazy"> здесь не годится: заглушки лежат
       * в ленте с overflow: hidden, часть карточек всегда стоит за
       * краем, а слайдер их ещё и переставляет. На живой витрине
       * браузер грузил то первую обложку, то остальные четыре — от
       * прогона к прогону по-разному. Поэтому решение о загрузке
       * принимаем сами и в один заход на всю ленту.
       */
      watchPoster() {
        const src = this.dataset.poster;
        if (!src || this.querySelector('.video-facade__poster')) return;

        const load = () => {
          if (this.querySelector('.video-facade__poster')) return;

          const img = document.createElement('img');

          // maxresdefault есть не у каждого ролика: YouTube делает её
          // только для достаточно крупного исходника. Если не отдал —
          // подставляем hqdefault, она есть всегда.
          const backup = this.dataset.posterBackup;
          if (backup) {
            img.addEventListener(
              'error',
              () => {
                img.src = backup;
              },
              { once: true }
            );
          }

          img.src = src;
          img.alt = this.dataset.title || '';
          img.width = 1280;
          img.height = 720;
          img.className = 'video-facade__poster';

          // Перед кнопкой: кнопка обязана остаться поверх обложки.
          this.prepend(img);
        };

        if (!('IntersectionObserver' in window)) {
          load();
          return;
        }

        const лента = this.closest('.video-slider-list');
        const секция = this.closest('.video-slider') || this;

        /*
         * Своя очередь на загрузку, без IntersectionObserver по карточке.
         *
         * Наблюдатель по карточке здесь врёт: лента с overflow: hidden,
         * часть карточек всегда за краем, и «появиться» они не могут.
         * Поэтому считаем сами: карточка близко, если её левый край
         * попадает в текущий вид ленты плюс ещё один экран вперёд.
         *
         * Разом всё не грузим: обложка ролика у YouTube весит около
         * ста килобайт, а карточек в ленте десяток. Мегабайт за раз
         * на блоке, до которого ещё надо долистать, — расточительство,
         * которое мы сами же ставили в упрёк главной.
         */
        const близко = () => {
          if (!лента) return true;

          // Считаем по видимым прямоугольникам, а не по offsetLeft:
          // offsetLeft отсчитывается от ближайшего позиционированного
          // предка, а он здесь не лента, и число выходит бессмысленным.
          const своя = this.getBoundingClientRect();
          const её = лента.getBoundingClientRect();

          return своя.left < её.right + её.width;
        };

        const проверить = () => {
          if (!близко()) return;
          load();
          лента && лента.removeEventListener('scroll', проверить);
          return true;
        };

        const observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;

              // Лента подошла к экрану: грузим то, что видно сейчас,
              // остальное — по мере прокрутки вбок.
              if (!проверить() && лента) {
                лента.addEventListener('scroll', проверить, { passive: true });
              }

              observer.disconnect();
            }
          },
          { rootMargin: '400px' }
        );

        observer.observe(секция);
      }

      load() {
        if (this.dataset.loaded) return;

        const src = this.dataset.embed;
        if (!src) return;

        this.dataset.loaded = 'true';

        const frame = document.createElement('iframe');
        frame.src = src;
        frame.title = this.dataset.title || 'Video';
        frame.loading = 'lazy';
        frame.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
        frame.allowFullscreen = true;
        frame.className = 'video-facade__frame';

        this.replaceChildren(frame);
      }
    }
  );
}
